import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Search, Home, ArrowLeft, UserCheck, Building2, Briefcase, Info } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

import { MEXICAN_CITIES as SEARCH_CITIES } from "@/constants/mexicanCities";

// Predictive City Search Component
const CitySearchSelect = ({ value, onChange }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const filteredCities = useMemo(() => {
    if (searchTerm.length < 4) return SEARCH_CITIES;
    return SEARCH_CITIES.filter(city => 
      city.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm]);

  const handleSelect = (city) => {
    onChange(city);
    setSearchTerm("");
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <div 
        className="flex items-center border border-gray-200 rounded-xl h-12 px-3 cursor-pointer bg-white"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Search className="w-4 h-4 text-gray-400 mr-2" />
        <input
          type="text"
          placeholder={value || "¿En qué ciudad buscas propiedad?"}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onClick={(e) => e.stopPropagation()}
          className="flex-1 outline-none text-[#384451] placeholder:text-gray-400"
          data-testid="city-search-input"
        />
        {value && !searchTerm && (
          <span className="text-[#384451] font-medium">{value}</span>
        )}
      </div>
      
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-60 overflow-y-auto">
          {searchTerm.length > 0 && searchTerm.length < 4 && (
            <div className="px-4 py-3 text-sm text-gray-500">
              Escribe al menos 4 caracteres para buscar...
            </div>
          )}
          {filteredCities.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500">
              No se encontraron ciudades
            </div>
          ) : (
            filteredCities.map(city => (
              <div
                key={city}
                className={`px-4 py-3 cursor-pointer hover:bg-gray-50 text-[#384451] ${value === city ? "bg-primary/10 font-medium" : ""}`}
                onClick={() => handleSelect(city)}
                data-testid={`city-option-${city.replace(/\s/g, '-')}`}
              >
                {city}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const initialRoleFromUrl = searchParams.get("role");
  const modeFromUrl = searchParams.get("mode"); // 'login' or 'register'
  const [isLogin, setIsLogin] = useState(modeFromUrl !== "register" && !initialRoleFromUrl);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState(initialRoleFromUrl || "shopper");
  const [phoneWhatsapp, setPhoneWhatsapp] = useState("");
  const [searchCity, setSearchCity] = useState("");

  useEffect(() => {
    // If a role hint comes via URL (e.g. ?role=owner from "Vende tu Propiedad" CTA),
    // default to register tab with that role pre-selected.
    if (initialRoleFromUrl && ["shopper", "owner", "agent"].includes(initialRoleFromUrl)) {
      setRole(initialRoleFromUrl);
      setIsLogin(false);
    }
  }, [initialRoleFromUrl]);
  
  // Agent-specific fields
  const [agencyName, setAgencyName] = useState("");
  const [operatingCity, setOperatingCity] = useState("");
  const [interestCity, setInterestCity] = useState("");
  const [approximateInventory, setApproximateInventory] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate shopper-specific fields
    if (!isLogin && role === "shopper") {
      if (!phoneWhatsapp) {
        toast.error("El número de WhatsApp es requerido para compradores");
        return;
      }
      if (!searchCity) {
        toast.error("La ciudad de búsqueda es requerida para compradores");
        return;
      }
    }
    
    // Validate agent-specific fields
    if (!isLogin && role === "agent") {
      if (!phoneWhatsapp) {
        toast.error("El teléfono es requerido");
        return;
      }
      if (!agencyName) {
        toast.error("El nombre de la agencia es requerido");
        return;
      }
      if (!operatingCity) {
        toast.error("La ciudad principal de operación es requerida");
        return;
      }
      if (!interestCity) {
        toast.error("La ciudad de interés es requerida");
        return;
      }
      if (!acceptTerms) {
        toast.error("Debes aceptar los términos y condiciones");
        return;
      }
    }
    
    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      
      let data;
      if (isLogin) {
        data = { email, password };
      } else if (role === "shopper") {
        data = { 
          email, 
          password, 
          name, 
          role,
          phone_whatsapp: phoneWhatsapp,
          search_city: searchCity
        };
      } else if (role === "agent") {
        data = { 
          email, 
          password, 
          name, 
          role,
          phone_whatsapp: phoneWhatsapp,
          agency_name: agencyName,
          operating_city: operatingCity,
          interest_city: interestCity,
          approximate_inventory: approximateInventory ? parseInt(approximateInventory) : 0,
          accept_terms: acceptTerms
        };
      } else {
        data = { email, password, name, role };
      }
      
      // Don't carry stale Authorization from a previous (logged-out) session.
      // This was causing intermittent 401s that only a full reload would fix.
      const prevAuth = axios.defaults.headers.common["Authorization"];
      delete axios.defaults.headers.common["Authorization"];

      let response;
      try {
        response = await axios.post(`${API}${endpoint}`, data);
      } catch (err) {
        // Restore prior header so other in-flight calls don't lose context
        if (prevAuth) axios.defaults.headers.common["Authorization"] = prevAuth;
        throw err;
      }
      
      // Handle login response with membership info
      if (isLogin && response.data.membership) {
        const membershipInfo = response.data.membership;
        login(response.data.access_token, response.data.user, membershipInfo);
        
        // Show appropriate message based on membership status
        if (membershipInfo.status === "pending_approval") {
          toast.warning("Tu cuenta está pendiente de aprobación por un administrador.");
        } else if (membershipInfo.status === "grace_period") {
          toast.warning(membershipInfo.message || "Tu periodo de prueba ha expirado. Activa tu suscripción pronto.");
        } else if (membershipInfo.status === "expiring_soon") {
          toast.info(membershipInfo.message || "Tu suscripción expira pronto.");
        } else if (membershipInfo.status === "inactive") {
          toast.error(membershipInfo.message || "Tu cuenta ha sido desactivada por falta de pago.");
        } else {
          toast.success("¡Bienvenido de nuevo!");
        }
      } else {
        login(response.data.access_token, response.data.user);
        
        // Show different message for agents on registration (pending approval)
        if (!isLogin && role === "agent") {
          toast.success("Terminaste tu registro, en breve recibirás un aviso cuando un administrador la apruebe.", {
            duration: 8000,
            icon: "📧"
          });
        } else {
          toast.success(isLogin ? "¡Bienvenido de nuevo!" : "¡Cuenta creada exitosamente!");
        }
      }
      
      // Handle navigation based on role and membership status
      const userRole = response.data.user.role;
      const membershipStatus = response.data.membership?.status;
      
      // For agents/owners with inactive accounts, still allow them to see dashboard (to pay)
      if (userRole === "shopper") navigate("/onboarding");
      else if (userRole === "owner") navigate("/owner-dashboard");
      else if (userRole === "agent") navigate("/agent-dashboard");
      else if (userRole === "admin") navigate("/admin-dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error de autenticación");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-md mx-auto mb-4">
        <Link to="/" data-testid="auth-back-home"
          className="inline-flex items-center gap-2 text-sm text-[#384451]/70 hover:text-[#384451] transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <Home className="w-4 h-4" />
          Volver a la página principal
        </Link>
      </div>
      <div className="flex items-center justify-center">
      <Card className="w-full max-w-md modern-card border-0" data-testid="auth-card">
        <CardHeader className="text-center space-y-2 pb-6 pt-8">
          <img
            src="https://customer-assets.emergentagent.com/job_propswipe-7/artifacts/65kacgp6_Isotipo%20Findperty-2.png"
            alt="Findperty"
            className="mx-auto mb-4"
            style={{ width: '135px', height: '75px', objectFit: 'contain' }}
          />
          <CardTitle className="text-3xl font-semibold text-[#384451]">{isLogin ? "Bienvenido de Nuevo" : "Crear Cuenta"}</CardTitle>
          <CardDescription className="text-base text-[#384451]/70">
            {isLogin ? "Inicia sesión para continuar tu viaje" : "Únete a Findperty hoy"}
          </CardDescription>
        </CardHeader>
        <CardContent className="pb-8">
          {/* Role-based instructions for registration */}
          {!isLogin && (
            <div className="mb-5 p-4 rounded-xl bg-[#CCD500]/10 border border-[#CCD500]/30" data-testid="role-instructions">
              <div className="flex items-start gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-[#CCD500] flex items-center justify-center shrink-0">
                  {role === "shopper" && <UserCheck className="w-5 h-5 text-[#384451]" />}
                  {role === "owner" && <Building2 className="w-5 h-5 text-[#384451]" />}
                  {role === "agent" && <Briefcase className="w-5 h-5 text-[#384451]" />}
                </div>
                <div className="flex-1 text-sm">
                  {role === "shopper" && (
                    <>
                      <p className="font-bold text-[#384451] mb-1">Cuenta de Comprador</p>
                      <p className="text-[#384451]/80 leading-relaxed">
                        Gratis para siempre. Selecciona tu rol abajo, completa tus datos y empieza a hacer match con propiedades compatibles con tu estilo de vida.
                      </p>
                    </>
                  )}
                  {role === "owner" && (
                    <>
                      <p className="font-bold text-[#384451] mb-1">Cuenta de Propietario (Vendedor)</p>
                      <p className="text-[#384451]/80 leading-relaxed">
                        Sin tarjeta, sin membresía. Crea tu cuenta, carga tu propiedad y acepta el acuerdo de comisión con el Master Partner de tu zona al cerrar la venta.
                      </p>
                    </>
                  )}
                  {role === "agent" && (
                    <>
                      <p className="font-bold text-[#384451] mb-1">Cuenta de Agente Inmobiliario</p>
                      <p className="text-[#384451]/80 leading-relaxed">
                        Acceso a 8 módulos profesionales con 7 días de prueba gratis. Sin tarjeta. Luego elige el plan que se ajusta a tu operación.
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          {isLogin && (
            <div className="mb-5 p-3 rounded-xl bg-blue-50 border border-blue-200" data-testid="login-help">
              <div className="flex items-start gap-2 text-xs text-blue-900">
                <Info className="w-4 h-4 mt-0.5 shrink-0" />
                <p className="leading-relaxed">
                  Ingresa con el correo y contraseña de tu cuenta. Si aún no tienes una, presiona <strong>{`"Regístrate"`}</strong> abajo y elige tu perfil.
                </p>
              </div>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <Input
                  placeholder="Nombre Completo"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  data-testid="name-input"
                  className="h-12 rounded-xl border-gray-200"
                />
                
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-white text-[#384451]"
                  data-testid="role-select"
                >
                  <option value="shopper">Comprador</option>
                  <option value="owner">Propietario (Vendedor)</option>
                  <option value="agent">Agente Inmobiliario</option>
                </select>
              </>
            )}
            
            <Input
              type="email"
              placeholder="Correo Electrónico"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="email-input"
              className="h-12 rounded-xl border-gray-200"
            />
            
            <Input
              type="password"
              placeholder="Contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="password-input"
              className="h-12 rounded-xl border-gray-200"
            />

            {/* Shopper-specific fields */}
            {!isLogin && role === "shopper" && (
              <>
                <Input
                  type="tel"
                  placeholder="WhatsApp (10 dígitos)"
                  value={phoneWhatsapp}
                  onChange={(e) => setPhoneWhatsapp(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  required
                  data-testid="phone-input"
                  className="h-12 rounded-xl border-gray-200"
                />
                
                <CitySearchSelect value={searchCity} onChange={setSearchCity} />
                
                <p className="text-xs text-[#384451]/60 -mt-2">
                  Estos datos nos ayudan a encontrar propiedades en tu zona
                </p>
              </>
            )}

            {/* Agent-specific fields */}
            {!isLogin && role === "agent" && (
              <div className="space-y-4 p-4 bg-purple-50 rounded-xl border border-purple-100">
                <div className="flex items-center gap-2 mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                  <span className="font-semibold text-purple-800">Datos del Agente Inmobiliario</span>
                </div>
                
                <Input
                  type="tel"
                  placeholder="Teléfono de contacto"
                  value={phoneWhatsapp}
                  onChange={(e) => setPhoneWhatsapp(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  required
                  data-testid="agent-phone-input"
                  className="h-12 rounded-xl border-gray-200 bg-white"
                />
                
                <Input
                  placeholder="Nombre de tu Agencia"
                  value={agencyName}
                  onChange={(e) => setAgencyName(e.target.value)}
                  required
                  data-testid="agency-name-input"
                  className="h-12 rounded-xl border-gray-200 bg-white"
                />
                
                <div>
                  <label className="text-sm text-purple-700 mb-1 block">Ciudad principal donde operas</label>
                  <CitySearchSelect value={operatingCity} onChange={setOperatingCity} />
                </div>
                
                <div>
                  <label className="text-sm text-purple-700 mb-1 block">Ciudad de interés para operar</label>
                  <CitySearchSelect value={interestCity} onChange={setInterestCity} />
                  <p className="text-xs text-purple-600 mt-1 flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="16" x2="12" y2="12"></line>
                      <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                    Sujeto a aprobación según criterios de distribución de agencias
                  </p>
                </div>
                
                <div>
                  <label className="text-sm text-purple-700 mb-1 block">Número aproximado de propiedades en tu inventario</label>
                  <select
                    value={approximateInventory}
                    onChange={(e) => setApproximateInventory(e.target.value)}
                    className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-white text-[#384451]"
                    data-testid="inventory-select"
                  >
                    <option value="">Selecciona un rango</option>
                    <option value="5">1 - 5 propiedades</option>
                    <option value="15">6 - 15 propiedades</option>
                    <option value="30">16 - 30 propiedades</option>
                    <option value="50">31 - 50 propiedades</option>
                    <option value="100">Más de 50 propiedades</option>
                  </select>
                </div>
                
                <div className="flex items-start gap-3 mt-4 p-3 bg-white rounded-lg border border-purple-200">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="mt-1 w-5 h-5 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                    data-testid="terms-checkbox"
                  />
                  <div className="text-sm text-[#384451]">
                    <p>Acepto los <a href="/terminos" className="text-purple-600 underline font-medium">Términos y Condiciones</a> de uso y contratación de la plataforma Findperty.</p>
                    <p className="text-xs text-purple-600 mt-1">Al registrarte se activa tu suscripción Freemium (3 días de prueba, hasta 3 propiedades)</p>
                  </div>
                </div>
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12 rounded-xl bg-primary hover:bg-[#b8c100] text-[#384451] font-semibold"
              data-testid="auth-submit-btn"
            >
              {isLogin ? "Iniciar Sesión" : "Crear Cuenta"}
            </Button>
          </form>

          <p className="text-center mt-6 text-sm text-[#384451]/70">
            {isLogin ? "¿No tienes cuenta? " : "¿Ya tienes cuenta? "}
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-primary font-semibold hover:underline"
              data-testid="toggle-auth-btn"
            >
              {isLogin ? "Regístrate" : "Inicia Sesión"}
            </button>
          </p>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};